/**
 * Utility functions for "What's Happening Today" feature
 */

/**
 * Get the current day of week (0 = Sunday, 6 = Saturday)
 * Matches the active_days format used in business data
 */
export const getTodayDayNumber = () => {
  return new Date().getDay(); // 0-6, where 0 is Sunday
};

/**
 * Check if a date string is today
 * @param {string} dateString - ISO date string
 * @returns {boolean}
 */
export const isToday = (dateString) => {
  if (!dateString) return false;

  try {
    const date = new Date(dateString);
    const today = new Date();

    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  } catch (e) {
    return false;
  }
};

/**
 * Filter businesses that are open today
 * @param {Array} businesses - Array of business objects
 * @returns {Array} - Businesses open today
 */
export const getBusinessesOpenToday = (businesses) => {
  if (!Array.isArray(businesses)) return [];

  const todayDayNumber = getTodayDayNumber();

  return businesses.filter(business => {
    // Only check regular businesses (not events or barters)
    if (business.type === 'event' || business.type === 'barter') {
      return false;
    }

    // Only show businesses that have active_days defined and include today
    if (Array.isArray(business.active_days) && business.active_days.length > 0) {
      return business.active_days.includes(todayDayNumber);
    }

    // If no active_days specified, don't show in "What's Happening Today"
    return false;
  });
};

/**
 * Filter events happening today
 * @param {Array} events - Array of event objects
 * @returns {Array} - Events happening today
 */
export const getEventsToday = (events) => {
  if (!Array.isArray(events)) return [];

  const todayDayNumber = getTodayDayNumber();

  return events.filter(event => {
    // Must have event_time or be type 'event'
    if (!event.event_time && event.type !== 'event') {
      return false;
    }

    // Check if event is happening today based on event_time
    const happeningToday = isToday(event.event_time);

    // If event has active_days defined, also check if today is included
    if (Array.isArray(event.active_days) && event.active_days.length > 0) {
      return happeningToday && event.active_days.includes(todayDayNumber);
    }

    // If no active_days specified, just check if event_time is today
    return happeningToday;
  });
};

/**
 * Check if user explicitly chose to hide the popup today
 * @returns {boolean} - True if user chose "don't show today"
 */
export const userHidPopupToday = () => {
  const hideUntil = localStorage.getItem('whatsHappeningPopup_hideUntil');
  const today = new Date().toDateString();
  const isHidden = hideUntil === today;

  console.log('🔍 Checking if user hid popup today:', { hideUntil, today, isHidden });

  if (!hideUntil) return false;
  return isHidden;
};

/**
 * Check if we should show the "What's Happening Today" popup
 * Uses localStorage to prevent showing multiple times per day
 *
 * @param {string} contextKey - Unique key for the context (e.g., 'app', 'zone_123')
 * @returns {boolean} - True if we should show the popup
 */
export const shouldShowWhatsHappeningPopup = (contextKey = 'app') => {
  // First check if user explicitly chose to hide it today
  const userHid = userHidPopupToday();
  if (userHid) {
    console.log('❌ Not showing popup - user chose to hide it today');
    return false;
  }

  const storageKey = `whatsHappening_lastShown_${contextKey}`;
  const lastShown = localStorage.getItem(storageKey);

  if (!lastShown) {
    console.log('✅ Showing popup - never shown before');
    return true;
  }

  try {
    const lastShownDate = new Date(lastShown);
    const today = new Date();

    // Show if last shown was on a different day
    const shouldShow = (
      lastShownDate.getFullYear() !== today.getFullYear() ||
      lastShownDate.getMonth() !== today.getMonth() ||
      lastShownDate.getDate() !== today.getDate()
    );

    console.log(shouldShow ? '✅ Showing popup - last shown on different day' : '❌ Not showing popup - already shown today', { lastShown, contextKey });
    return shouldShow;
  } catch (e) {
    // If there's an error parsing the date, show the popup
    console.log('✅ Showing popup - error parsing last shown date');
    return true;
  }
};

/**
 * Mark that we've shown the popup for this context today
 * @param {string} contextKey - Unique key for the context (e.g., 'app', 'zone_123')
 */
export const markWhatsHappeningPopupShown = (contextKey = 'app') => {
  const storageKey = `whatsHappening_lastShown_${contextKey}`;
  localStorage.setItem(storageKey, new Date().toISOString());
};

/**
 * Fetch businesses and events for a zone and determine what's happening today
 * @param {string} zoneId - Zone ID to fetch data for
 * @param {string} chatId - User's chat ID
 * @param {Function} fetchFn - Fetch function (e.g., cacheSafeFetch or safeFetch)
 * @returns {Promise<Object>} - Object with openBusinesses and todayEvents arrays
 */
export const fetchWhatsHappeningToday = async (zoneId, chatId, fetchFn) => {
  try {
    const apiBase = 'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default';

    // Fetch all businesses (includes all types)
    const businessesRes = await fetchFn(
      `${apiBase}/businesses?zone_id=${zoneId}`,
      chatId,
      { method: 'GET' }
    );

    if (!businessesRes.ok) {
      console.error('Failed to fetch businesses:', businessesRes.statusText);
      return { openBusinesses: [], todayEvents: [] };
    }

    const businessesData = await businessesRes.json();

    // The API returns {businesses: [...]} format
    const allItems = Array.isArray(businessesData)
      ? businessesData
      : (businessesData.businesses || []);

    console.log('📊 Fetched items for zone:', { zoneId, totalItems: allItems.length, items: allItems });

    // Separate events and regular businesses
    const events = allItems.filter(item => item.event_time || item.type === 'event');
    const businesses = allItems.filter(item => !item.event_time && item.type !== 'event' && item.type !== 'barter');

    console.log('📊 Separated items:', {
      eventsCount: events.length,
      businessesCount: businesses.length,
      businesses: businesses.map(b => ({ name: b.business_name, active_days: b.active_days }))
    });

    // Filter to get only what's happening today
    const openBusinesses = getBusinessesOpenToday(businesses);
    const todayEvents = getEventsToday(events);

    console.log('✅ What\'s happening today:', {
      openBusinessesCount: openBusinesses.length,
      todayEventsCount: todayEvents.length,
      todayDayNumber: getTodayDayNumber(),
      openBusinesses: openBusinesses.map(b => ({ name: b.business_name, active_days: b.active_days }))
    });

    return {
      openBusinesses,
      todayEvents
    };
  } catch (error) {
    console.error('Error fetching what\'s happening today:', error);
    return { openBusinesses: [], todayEvents: [] };
  }
};

/**
 * Fetch what's happening today across all user's zones
 * @param {string} chatId - User's chat ID
 * @param {Function} fetchFn - Fetch function (e.g., cacheSafeFetch or safeFetch)
 * @returns {Promise<Object>} - Object with openBusinesses and todayEvents arrays
 */
export const fetchWhatsHappeningInUserZones = async (chatId, fetchFn) => {
  try {
    const apiBase = 'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default';

    // Get user's zones
    const userLocalsRes = await fetchFn(
      `${apiBase}/user-locals?chat_id=${chatId}`,
      chatId,
      { method: 'GET' }
    );

    if (!userLocalsRes.ok) {
      console.error('Failed to fetch user zones:', userLocalsRes.statusText);
      return { openBusinesses: [], todayEvents: [] };
    }

    const userLocalsData = await userLocalsRes.json();
    const userZoneIds = Array.isArray(userLocalsData)
      ? userLocalsData.map(z => z.zone_id)
      : [];

    if (userZoneIds.length === 0) {
      return { openBusinesses: [], todayEvents: [] };
    }

    // Fetch businesses/events from all user's zones
    const promises = userZoneIds.map(zoneId =>
      fetchWhatsHappeningToday(zoneId, chatId, fetchFn)
    );

    const results = await Promise.all(promises);

    // Combine all results and deduplicate by business_id
    const allOpenBusinesses = [];
    const allTodayEvents = [];
    const seenBusinessIds = new Set();
    const seenEventIds = new Set();

    results.forEach(result => {
      result.openBusinesses.forEach(business => {
        if (!seenBusinessIds.has(business.business_id)) {
          seenBusinessIds.add(business.business_id);
          allOpenBusinesses.push(business);
        }
      });

      result.todayEvents.forEach(event => {
        if (!seenEventIds.has(event.business_id)) {
          seenEventIds.add(event.business_id);
          allTodayEvents.push(event);
        }
      });
    });

    return {
      openBusinesses: allOpenBusinesses,
      todayEvents: allTodayEvents
    };
  } catch (error) {
    console.error('Error fetching what\'s happening in user zones:', error);
    return { openBusinesses: [], todayEvents: [] };
  }
};
